// services/eventWizardApi.js
import axios from 'axios';

export function authHeaders() {
  return { headers: { 'auth-token': localStorage.getItem('auth-token') } };
}

export function ewList(backendUrl) {
  return axios.get(`${backendUrl}/event-wizard/list`, authHeaders());
}
export function ewLaunch(backendUrl, payload) {
  // payload may include controllerChannel ('stable'|'dev') — see launchInstances
  return axios.post(`${backendUrl}/event-wizard/launch`, payload, authHeaders());
}
export function ewLaunchMaster(backendUrl, eventName, region, controllerChannel) {
  return axios.post(`${backendUrl}/event-wizard/launch-master`, { eventName, region, controllerChannel }, authHeaders());
}
export function ewTerminate(backendUrl, instanceId) {
  return axios.post(`${backendUrl}/event-wizard/terminate`, { instanceId }, authHeaders());
}
export function ewCheckRtmp(backendUrl, ip) {
  return axios.get(`${backendUrl}/event-wizard/check-rtmp?ip=${ip}`, authHeaders());
}
export function ewGetEvent(backendUrl, evt) {
  return axios.get(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}`, authHeaders());
}
export function ewResetEventSession(backendUrl, evt) {
  return axios.post(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/reset-session`, {}, authHeaders());
}
// Marks the event as ended: clears only the go epoch (the "running" switch).
// Unlike reset-session it leaves fragments/MPD/VOD intact — it runs at Stop
// Event, when those are exactly what the VOD needs.
export function ewEndEventSession(backendUrl, evt) {
  return axios.post(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/end-session`, {}, authHeaders());
}
export function ewDeleteEvent(backendUrl, evt) {
  return axios.delete(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}`, authHeaders());
}
export function ewSaveEventCmd(backendUrl, evt, payload) {
  return axios.post(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/command`, payload, authHeaders());
}
export function ewSaveMpd(backendUrl, evt, mpd) {
  return axios.post(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/mpd`, { mpd }, authHeaders());
}
export function ewSaveDashSettings(backendUrl, evt, dashSettings) {
  return axios.post(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/dash-settings`, { dashSettings }, authHeaders());
}
export function ewGlobalCommands(backendUrl) {
  return axios.get(`${backendUrl}/event-wizard/event/commands`, authHeaders());
}
export function ewSaveRoom(backendUrl, evt, roomKey) {
  return axios.post(`${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/room`, { roomKey }, authHeaders());
}
export function ewUpdateNodeScript(backendUrl, instanceId, command) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ffmpeg-script`,
    { command },
    authHeaders()
  );
}

// --- FFmpeg start/stop directo en el nodo
// `extra` travels in the body: pass { reuseEventEpoch: true } when restarting a
// single camera of a RUNNING event, so the backend hands it the event's existing
// grid epoch instead of minting a fresh one (which would also move the AST).
export function ewStartFfmpeg(backendUrl, instanceId, eventName, camName, extra = {}) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ffmpeg/start`,
    { eventName, camName, ...extra },
    authHeaders()
  );
}
export function ewStopFfmpeg(backendUrl, instanceId) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ffmpeg/stop`,
    {},
    authHeaders()
  );
}
// Archive the whole session server-side (backend fans out to each controller).
export function ewArchiveSession(backendUrl, evt, archiveTs) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/archive-session`,
    { archiveTs },
    authHeaders()
  );
}
// Point the Event's sources at the VOD just generated (inside archived_<ts>/).
export function ewPointToVod(backendUrl, evt, archiveTs, vodTimestamp) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/point-to-vod`,
    { archiveTs, vodTimestamp },
    authHeaders()
  );
}

// --- Synchronized start: prepare + go + ready
// archiveTs must be the SAME for every camera of a start so the previous session
// is archived into one shared archived_<ts>/ snapshot.
// The go body should carry a goRound key, also the SAME for every camera of one
// spawn round: the backend mints the event-start epoch (eventStartEpochMs, which
// every controller anchors its recovery grid on and which becomes the manifest
// AST) once per round, keyed on it.
export function ewPrepareFfmpeg(backendUrl, instanceId, eventName, camName, archiveTs) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ffmpeg/prepare`,
    { eventName, camName, archiveTs },
    authHeaders()
  );
}
export function ewGoFfmpeg(backendUrl, instanceId, body = {}) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ffmpeg/go`,
    body,
    authHeaders()
  );
}
export function ewFfmpegReady(backendUrl, instanceId) {
  // Polled in a loop during the start phases. The deadline keeps a box whose
  // controller is busy (blocked in a sync mount/cleanup) from hanging the
  // whole "Segments ready" phase — a timed-out attempt just retries next
  // cycle, and createdAtMs is stamped at production time on the box, so late
  // polls cannot distort the warm-delay math.
  return axios.get(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ffmpeg/ready`,
    { ...authHeaders(), timeout: 8000 }
  );
}

// --- publishTime updater on master node
export function ewPublishStart(backendUrl, instanceId, eventName) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/manifest/publish-start`,
    { eventName },
    authHeaders()
  );
}
export function ewPublishStop(backendUrl, instanceId) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/manifest/publish-stop`,
    {},
    authHeaders()
  );
}

// --- Proxy manifest en el master
export function ewGetManifest(backendUrl, instanceId, eventName) {
  const qs = eventName ? `?eventName=${encodeURIComponent(eventName)}` : '';
  return axios.get(
    `${backendUrl}/event-wizard/proxy/${instanceId}/manifest${qs}`,
    authHeaders()
  );
}
export function ewSetManifest(backendUrl, instanceId, mpd, eventName, mpd8K, mpdFull) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/manifest`,
    { mpd, eventName, mpd8K, mpdFull },
    authHeaders()
  );
}

// ===== FFmpeg Presets
export function ewListPresets(backendUrl) {
  return axios.get(`${backendUrl}/event-wizard/ffmpeg-presets`, authHeaders());
}
export function ewCreatePreset(backendUrl, payload) {
  return axios.post(`${backendUrl}/event-wizard/ffmpeg-presets`, payload, authHeaders());
}
export function ewUpdatePreset(backendUrl, id, payload) {
  return axios.put(`${backendUrl}/event-wizard/ffmpeg-presets/${id}`, payload, authHeaders());
}
export function ewDeletePreset(backendUrl, id) {
  return axios.delete(`${backendUrl}/event-wizard/ffmpeg-presets/${id}`, authHeaders());
}
export function ewSetDefaultPreset(backendUrl, id) {
  return ewUpdatePreset(backendUrl, id, { isDefault: true });
}

// ==== NUEVO: guardar perfil detectado de cámara (codecs, width, height, scale) ====
export function ewSaveCamProfile(backendUrl, evt, payload) {
  // payload: { instanceId, camName, codecs, width, height, scale }
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/cam-profile`,
    payload,
    authHeaders()
  );
}

// ==== NUEVO: pedir reconstrucción "oficial" del MPD en backend con perfiles ====
// Backend rebuilds all 3 variants, saves to DB, and pushes directly to master node
export function ewRebuildMpd(backendUrl, evt, camMpdData, masterInstanceId, { forcePtoZero = false, resetLatencyTarget = false } = {}) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/mpd-rebuild`,
    { camMpdData, masterInstanceId, forcePtoZero, resetLatencyTarget },
    authHeaders()
  );
}

// ==== Track order (IR-196) ====
// Saves the operator-defined cam/audio order for the manifest. Positional Event
// arrays (cam_names/cam_imgs/audio_names/audio_imgs) are permuted server-side so
// labels travel with their track. Call ewRebuildMpd afterwards to publish.
export function ewSetTrackOrder(backendUrl, evt, { camOrder, audioOrder } = {}) {
  return axios.patch(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/track-order`,
    { camOrder, audioOrder },
    authHeaders()
  );
}

// Generate VOD manifest (type="static") with multi-Period structure
export function ewGenerateVodMpd(backendUrl, evt, masterInstanceId) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/mpd-vod`,
    { masterInstanceId },
    authHeaders()
  );
}

// ADD: create / delete a cam's AdaptationSet in the event MPD

export function ewAddMpdFragment(backendUrl, evt, payload) {
  // payload: { instanceId, camName, role }
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/mpd-fragments`,
    payload,
    authHeaders()
  );
}

export function ewRemoveMpdFragment(backendUrl, evt, instanceId) {
  return axios.delete(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/mpd-fragments/${encodeURIComponent(instanceId)}`,
    authHeaders()
  );
}

export function ewToggleMpdFragment(backendUrl, evt, camName, role, enabled) {
  return axios.patch(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/mpd-fragments/toggle`,
    { camName, role, enabled },
    authHeaders()
  );
}

export function ewGetCamLabels(backendUrl, evt) {
  return axios.get(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/cam-labels`,
    authHeaders()
  );
}

export function ewSaveCamLabel(backendUrl, evt, instanceId, label) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/cam-label`,
    { instanceId, label },
    authHeaders()
  );
}

// ==== Content Offsets (per-camera PTO adjustment) ====
export function ewGetContentOffsets(backendUrl, evt) {
  return axios.get(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/content-offsets`,
    authHeaders()
  );
}

export function ewReanchorAst(backendUrl, evt, body) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/reanchor-ast`,
    body,
    authHeaders()
  );
}

export function ewSaveContentOffset(backendUrl, evt, camName, offsetSeconds) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/content-offset`,
    { camName, offsetSeconds },
    authHeaders()
  );
}

// ⚡ Low-latency mode per event (LL-DASH chunked): next rebuild emits ATO 0.8 +
// availabilityTimeComplete=false and the LL player preset lands in dashSettings.
// Transport (8090/8091) is NOT part of this — it travels in the ffmpeg preset URL.
export function ewSetLowLatency(backendUrl, evt, lowLatency) {
  return axios.put(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/low-latency`,
    { lowLatency },
    authHeaders()
  );
}

// ==== LiveKit (WHIP) path, parallel to DASH ====
export function ewSetStreamingMode(backendUrl, evt, mode) {
  return axios.put(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/streaming-mode`,
    { mode },
    authHeaders()
  );
}

export function ewGetLivekitIngress(backendUrl, evt) {
  return axios.get(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/livekit/ingress`,
    authHeaders()
  );
}

export function ewCreateLivekitIngress(backendUrl, evt, camName, participantName) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/livekit/ingress`,
    { camName, participantName },
    authHeaders()
  );
}

export function ewDeleteLivekitIngress(backendUrl, evt, camName) {
  return axios.delete(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/livekit/ingress/${encodeURIComponent(camName)}`,
    authHeaders()
  );
}

export function ewGetLivekitParticipants(backendUrl, evt) {
  return axios.get(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/livekit/participants`,
    authHeaders()
  );
}

// LiveKit slot on the instance — separate process from the DASH ffmpeg,
// so starting/stopping one never touches the other.
export function ewLivekitStart(backendUrl, instanceId) {
  return axios.post(`${backendUrl}/event-wizard/proxy/${instanceId}/livekit/start`, {}, authHeaders());
}
export function ewLivekitStop(backendUrl, instanceId) {
  return axios.post(`${backendUrl}/event-wizard/proxy/${instanceId}/livekit/stop`, {}, authHeaders());
}
export function ewLivekitStatus(backendUrl, instanceId) {
  return axios.get(`${backendUrl}/event-wizard/proxy/${instanceId}/livekit/status`, authHeaders());
}

export function ewCreateLivekitTestToken(backendUrl, evt) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/livekit/test-token`,
    {},
    authHeaders()
  );
}

export function ewApplyLivekitPreset(backendUrl, evt, { instanceId, camName, presetId, publicIp }) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/livekit/apply-preset`,
    { instanceId, camName, presetId, publicIp },
    authHeaders()
  );
}

// ==== SRS Config & Controller Source ====
export function ewGetSrsConfig(backendUrl, instanceId) {
  return axios.get(
    `${backendUrl}/event-wizard/proxy/${instanceId}/srs/config`,
    authHeaders()
  );
}
export function ewSetSrsConfig(backendUrl, instanceId, config) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/srs/config`,
    { config },
    authHeaders()
  );
}
export function ewGetControllerSource(backendUrl, instanceId) {
  return axios.get(
    `${backendUrl}/event-wizard/proxy/${instanceId}/controller/source`,
    authHeaders()
  );
}
export function ewSetControllerSource(backendUrl, instanceId, source) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/controller/source`,
    { source },
    authHeaders()
  );
}
export function ewApplyAll(backendUrl, payload) {
  return axios.post(
    `${backendUrl}/event-wizard/apply-all`,
    payload,
    authHeaders()
  );
}
export function ewRebootInstances(backendUrl, eventName, instanceId) {
  return axios.post(
    `${backendUrl}/event-wizard/reboot-instances`,
    { eventName, instanceId },   // instanceId opcional: reinicia solo ese servidor
    authHeaders()
  );
}

// Slate logo endpoints removed (IR-198): the recovery controller retired the
// slate chain — an ingest drop is ridden out by per-camera supervision and a
// -start_number relaunch, not by switching onto a slate feed.

// ==== Latency target ====
// Seconds. Becomes <Latency target> (ms) in the master manifest. The backend clamps
// it up to whatever the camera offsets and the failover floor require, so a value
// that is too low is corrected rather than obeyed.
export function ewSetLiveDelay(backendUrl, evt, liveDelay) {
  return axios.put(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/live-delay`,
    { liveDelay },
    authHeaders()
  );
}

// ==== Declared ingest profiles (persisted per event+camera in Mongo) ====
// Source of truth for the rig params. Pushed down to each instance's
// /ingest/config at event start, so a relaunched instance re-seeds itself.
export function ewGetIngestProfiles(backendUrl, evt) {
  return axios.get(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/ingest-profiles`,
    authHeaders()
  );
}
export function ewSaveNormalizerQuality(backendUrl, evt, quality) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/normalizer-quality`,
    quality,
    authHeaders()
  );
}

export function ewSaveIngestProfile(backendUrl, evt, camName, profile) {
  return axios.post(
    `${backendUrl}/event-wizard/event/${encodeURIComponent(evt)}/ingest-profile`,
    { camName, ...profile },
    authHeaders()
  );
}

// ==== Declared ingest config (per-instance copy on the controller) ====
// Rig parameters declared per instance so slate + normalizer can be sized before
// the camera publishes — what allows starting an event on the logo alone.
// Writes are deferred: they take effect at the next /ffmpeg/prepare.
export function ewGetIngestConfig(backendUrl, instanceId) {
  return axios.get(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ingest/config`,
    authHeaders()
  );
}
export function ewSetIngestConfig(backendUrl, instanceId, config) {
  return axios.post(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ingest/config`,
    config,
    authHeaders()
  );
}
export function ewClearIngestConfig(backendUrl, instanceId) {
  return axios.delete(
    `${backendUrl}/event-wizard/proxy/${instanceId}/ingest/config`,
    authHeaders()
  );
}

// Per-camera health/sync monitor (IR-198) — supersedes /drift, whose
// segmentsAhead/accumulatedDriftSeconds are null on the recovery controller.
export function ewGetMonitor(backendUrl, instanceId) {
  // Polled every MONITOR_POLL_MS per cam. The deadline keeps a slow/hung box
  // from pinning one of the browser's ~6 per-origin connections and queueing
  // every other poll behind it (frozen badges until page reload).
  return axios.get(
    `${backendUrl}/event-wizard/proxy/${instanceId}/monitor`,
    { ...authHeaders(), timeout: 8000 }
  );
}

// Elastic IPs: region-wide list + associate to an instance (force-reassoc)
export function ewListElasticIps(backendUrl) {
  return axios.get(`${backendUrl}/event-wizard/elastic-ips`, authHeaders());
}
export function ewAssociateEip(backendUrl, instanceId, allocationId) {
  return axios.post(`${backendUrl}/event-wizard/proxy/${instanceId}/associate-eip`, { allocationId }, authHeaders());
}

// Failover / manual-return / source-override endpoints removed (IR-198): the
// instance-side failover proxy is gone along with the slate chain.